import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';

// Components
import FilterMenu from '../components/FilterMenu';
import ChartCard from '../components/ChartCard';
import FilterPresetModal from '../components/FilterPresetModal';
import LoadingIndicator from '../components/LoadingIndicator';
import Pagination from '../components/Pagination';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  // URL query param support: /dashboard?q=NET,AVGO,MSFT
  // When present, we force a symbol filter and reset pagination.
  const searchParams = new URLSearchParams(location.search);
  const qSymbolsRaw = searchParams.get('q') || '';

  const [loading, setLoading] = useState(true);
  const [stocks, setStocks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('signal_score_desc'); // New: sorting state
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [filterPresets, setFilterPresets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState(null);
  const [isEstimatedCount, setIsEstimatedCount] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [summaryStats, setSummaryStats] = useState(null);
  const [dataFreshness, setDataFreshness] = useState(null);
  const pageSize = 18;

  // Fetch metadata (data freshness)
  const fetchMetadata = async () => {
    try {
      const metadata = await ApiService.getMetadata();
      if (metadata.last_updated) {
        const date = new Date(metadata.last_updated);
        setDataFreshness(date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }));
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  // Fetch summary statistics
  const fetchSummaryStats = async () => {
    try {
      const stats = await ApiService.getSummaryStats();
      setSummaryStats(stats);
    } catch (error) {
      console.error('Error fetching summary stats:', error);
      toast.error('Failed to load summary statistics');
    }
  };

  // Initialize data on first load
  useEffect(() => {
    fetchMetadata();
    fetchSummaryStats();
  }, []);

  // Fetch stock data with filters, sorting, and pagination
  const fetchStocks = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress('Loading data...');

      const response = await ApiService.getRecords(filters, currentPage, pageSize, sortBy);

      setIsEstimatedCount(response.isEstimatedCount || false);
      setStocks(response.data);
      setHasNextPage(response.hasNextPage || false);
      setHasPreviousPage(response.hasPreviousPage || false);
      setTotalCount(response.totalCount || null);
      
      if (response.data.length > 0 && columns.length === 0) {
        const columnNames = Object.keys(response.data[0]).filter(
          key => key !== 'id' && key !== 'createdTime'
        );
        setColumns(columnNames);
      }

      setLoading(false);
      setLoadingProgress('');
      
      console.log(`Dashboard: Loaded ${response.data.length} stocks, page ${currentPage}`);
      
    } catch (error) {
      console.error('Error fetching stocks:', error);
      setError(error.message);
      toast.error(`Failed to load stock data: ${error.message}`);
      setLoading(false);
      setLoadingProgress('');
      setStocks([]);
    }
  };

  // Fetch user's saved filter presets
  const fetchFilterPresets = async () => {
    if (!currentUser) return;
    
    try {
      const savedPresets = localStorage.getItem(`filter_presets_${currentUser.uid}`);
      if (savedPresets) {
        setFilterPresets(JSON.parse(savedPresets));
      }
    } catch (error) {
      console.error('Error fetching filter presets:', error);
    }
  };

  // Handle returning from chart detail view
  useEffect(() => {
    if (location.state) {
      const { filters: returnedFilters, page: returnedPage } = location.state;
      if (returnedFilters) {
        setFilters(returnedFilters);
      }
      if (returnedPage) {
        setCurrentPage(returnedPage);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Apply symbol filter from URL (q=SYM1,SYM2,...)
  useEffect(() => {
    const normalized = qSymbolsRaw
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);

    if (normalized.length > 0) {
      setFilters((prev) => ({ ...prev, symbol: normalized.join(',') }));
      setCurrentPage(1);
    } else {
      // If q is removed, also remove the symbol filter (but keep other filters)
      setFilters((prev) => {
        if (!prev || prev.symbol === undefined) return prev;
        const next = { ...prev };
        delete next.symbol;
        return next;
      });
    }
  }, [location.search]);

  // Initial data fetch
  useEffect(() => {
    fetchStocks();
    fetchFilterPresets();
  }, [currentUser]);

  // Refetch when filters, pagination, or sorting changes
  useEffect(() => {
    fetchStocks();
  }, [filters, currentPage, sortBy]);

  // Handle page navigation
  const goToNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const goToPreviousPage = () => {
    if (hasPreviousPage) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  // Apply filters
  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setFilterMenuOpen(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  // Smart filter presets with trend cohorts
  const applySmartFilter = (filterType) => {
    let smartFilters = {};
    
    switch (filterType) {
      case 'up_trends':
        smartFilters = { trend_state: 1 };
        break;
      case 'new_uptrends':
        smartFilters = { new_up_trend: 1 };
        break;
      case 'down_trends':
        smartFilters = { trend_state: 0 };
        break;
      case 'new_downtrends':
        smartFilters = { new_down_trend: 1 };
        break;
      case 'uptrend_strong':
        // Custom filter handled in FilterMenu - this is for tile clicks
        smartFilters = { trend_state: 1, signal_score: 3, predicted_signal_score: 3 };
        break;
      case 'uptrend_weakening':
        smartFilters = { trend_state: 1 };
        break;
      case 'uptrend_near_reversal':
        smartFilters = { trend_state: 1, predicted_signal_score: 0 };
        break;
      case 'downtrend_declining':
        smartFilters = { trend_state: 0 };
        break;
      case 'downtrend_improving':
        smartFilters = { trend_state: 0 };
        break;
      case 'downtrend_near_reversal':
        smartFilters = { trend_state: 0, predicted_signal_score: 5 };
        break;
      default:
        break;
    }
    
    setFilters(smartFilters);
    setCurrentPage(1);
    toast.info(`Applied ${filterType.replace(/_/g, ' ')} filter`);
  };

  // Save/load/delete filter presets
  const saveFilterPreset = async (presetName) => {
    if (!currentUser) {
      toast.error('You must be logged in to save filter presets');
      return;
    }
    
    try {
      const newPreset = {
        id: Date.now(),
        name: presetName,
        filters: filters
      };
      
      const updatedPresets = [...filterPresets, newPreset];
      localStorage.setItem(`filter_presets_${currentUser.uid}`, JSON.stringify(updatedPresets));
      setFilterPresets(updatedPresets);
      
      toast.success('Filter preset saved successfully');
      setPresetModalOpen(false);
    } catch (error) {
      console.error('Error saving filter preset:', error);
      toast.error('Failed to save filter preset');
    }
  };

  const loadFilterPreset = (preset) => {
    setFilters(preset.filters);
    setCurrentPage(1);
    toast.info(`Loaded filter preset: ${preset.name}`);
  };

  const deleteFilterPreset = async (presetId) => {
    try {
      const updatedPresets = filterPresets.filter(p => p.id !== presetId);
      localStorage.setItem(`filter_presets_${currentUser.uid}`, JSON.stringify(updatedPresets));
      setFilterPresets(updatedPresets);
      toast.success('Filter preset deleted');
    } catch (error) {
      console.error('Error deleting filter preset:', error);
      toast.error('Failed to delete filter preset');
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Filter Toggle Button */}
      <button
        onClick={() => setFilterMenuOpen(!filterMenuOpen)}
        className="fixed top-24 left-4 z-30 bg-brand-500 text-white p-3 rounded-full shadow-lg hover:bg-brand-600 focus:outline-none transition-all"
        aria-label="Toggle Filter Menu"
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      </button>

      {/* Filter Menu */}
      <FilterMenu
        isOpen={filterMenuOpen}
        onClose={() => setFilterMenuOpen(false)}
        columns={columns}
        currentFilters={filters}
        onApplyFilters={applyFilters}
        onClearFilters={clearFilters}
        onSavePreset={() => setPresetModalOpen(true)}
        presets={filterPresets}
        onLoadPreset={loadFilterPreset}
        onDeletePreset={deleteFilterPreset}
      />

      {/* Save Filter Preset Modal */}
      {presetModalOpen && (
        <FilterPresetModal
          onClose={() => setPresetModalOpen(false)}
          onSave={saveFilterPreset}
        />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        
        {/* Data Freshness Banner */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-signal-bullish rounded-full animate-pulse mr-3"></div>
            <span className="text-sm text-gray-700">
              <span className="font-semibold">Data Last Updated:</span> {dataFreshness}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Refreshes nightly
          </div>
        </div>

        {/* Summary Dashboard with Trend Cohorts */}
        {summaryStats && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">Market Overview</h2>
            
            {/* Top Row - Total and Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* Total Stocks */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Stocks</div>
                <div className="text-2xl font-bold text-navy-900">{summaryStats.total.toLocaleString()}</div>
              </div>

              {/* New Uptrends */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => applySmartFilter('new_uptrends')}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">New Uptrends</div>
                <div className="text-2xl font-bold text-brand-500">{summaryStats.newUptrends}</div>
              </div>

              {/* New Downtrends */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => applySmartFilter('new_downtrends')}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">New Downtrends</div>
                <div className="text-2xl font-bold text-signal-bearish">{summaryStats.newDowntrends}</div>
              </div>
            </div>

            {/* Trend Cohorts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* UP Trend Cohort */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-md border-2 border-green-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-green-800">UP Trend Cohort</h3>
                  <div className="text-2xl font-bold text-green-700">{summaryStats.upTrend.total}</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {/* UpTrend-Strong */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-green-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('uptrend_strong')}
                  >
                    <div className="text-xs text-green-600 font-semibold mb-1">Strong</div>
                    <div className="text-xl font-bold text-green-700">{summaryStats.upTrend.strong}</div>
                    <div className="text-xs text-gray-500 mt-1">SS≥3 & PS≥3</div>
                  </div>

                  {/* UpTrend-Weakening */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-green-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('uptrend_weakening')}
                  >
                    <div className="text-xs text-green-600 font-semibold mb-1">Weakening</div>
                    <div className="text-xl font-bold text-yellow-600">{summaryStats.upTrend.weakening}</div>
                    <div className="text-xs text-gray-500 mt-1">SS&lt;3 & PS&lt;3</div>
                  </div>

                  {/* UpTrend-NearReversal */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-green-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('uptrend_near_reversal')}
                  >
                    <div className="text-xs text-green-600 font-semibold mb-1">Near Reversal</div>
                    <div className="text-xl font-bold text-red-600">{summaryStats.upTrend.nearReversal}</div>
                    <div className="text-xs text-gray-500 mt-1">SS≥3 & PS=0</div>
                  </div>
                </div>
              </div>

              {/* DOWN Trend Cohort */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg shadow-md border-2 border-red-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-red-800">DOWN Trend Cohort</h3>
                  <div className="text-2xl font-bold text-red-700">{summaryStats.downTrend.total}</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {/* DownTrend-Declining */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-red-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('downtrend_declining')}
                  >
                    <div className="text-xs text-red-600 font-semibold mb-1">Declining</div>
                    <div className="text-xl font-bold text-red-700">{summaryStats.downTrend.declining}</div>
                    <div className="text-xs text-gray-500 mt-1">SS&lt;3 & PS&lt;3</div>
                  </div>

                  {/* DownTrend-Improving */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-red-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('downtrend_improving')}
                  >
                    <div className="text-xs text-red-600 font-semibold mb-1">Improving</div>
                    <div className="text-xl font-bold text-yellow-600">{summaryStats.downTrend.improving}</div>
                    <div className="text-xs text-gray-500 mt-1">SS&gt;3 & PS&gt;3</div>
                  </div>

                  {/* DownTrend-NearReversal */}
                  <div 
                    className="bg-white rounded-lg shadow-sm border border-red-300 p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => applySmartFilter('downtrend_near_reversal')}
                  >
                    <div className="text-xs text-red-600 font-semibold mb-1">Near Reversal</div>
                    <div className="text-xl font-bold text-green-600">{summaryStats.downTrend.nearReversal}</div>
                    <div className="text-xs text-gray-500 mt-1">SS&gt;3 & PS=5</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Filters - Trend Based */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Filters</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => applySmartFilter('up_trends')} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-500 hover:text-white transition-colors">
              Up Trends
            </button>
            <button onClick={() => applySmartFilter('new_uptrends')} className="px-4 py-2 bg-brand-100 text-brand-700 rounded-lg text-sm font-medium hover:bg-brand-500 hover:text-white transition-colors">
              New Uptrends
            </button>
            <button onClick={() => applySmartFilter('down_trends')} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-500 hover:text-white transition-colors">
              Down Trends
            </button>
            <button onClick={() => applySmartFilter('new_downtrends')} className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg text-sm font-medium hover:bg-pink-500 hover:text-white transition-colors">
              New Downtrends
            </button>
            {Object.keys(filters).length > 0 && (
              <button onClick={clearFilters} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                Clear Filters ✕
              </button>
            )}
          </div>
        </div>

        {/* Active Filters Chips */}
        {Object.keys(filters).length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters)
                .filter(([_, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
                .map(([k, v]) => {
                  let label = `${k}: ${Array.isArray(v) ? v.join('-') : String(v)}`;
                  if (k === 'symbol') label = `Symbols: ${String(v).toUpperCase()}`;
                  if (k === 'is_etf') {
                    const sv = String(v);
                    if (sv === '1' || sv.toLowerCase() === 'true' || sv === 'etf_only') label = 'ETF only';
                    else if (sv === '0' || sv.toLowerCase() === 'false') label = 'Stocks only';
                    else label = `ETF: ${sv}`;
                  }
                  return (
                    <span key={k} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200">
                      {label}
                    </span>
                  );
                })}
            </div>
          </div>
        )}

        {/* Header with Sorting */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy-900 mb-2">Stock Signals</h1>
            <p className="text-gray-600">
              {Object.keys(filters).length > 0
                ? totalCount !== null 
                  ? `${totalCount.toLocaleString()} filtered results`
                  : 'Filtered results'
                : totalCount !== null 
                  ? `${totalCount.toLocaleString()} total stocks`
                  : 'All stocks'}
            </p>
          </div>

          {/* Sorting Dropdown - Trend Categories */}
          <div className="mt-4 sm:mt-0">
            <label htmlFor="sort" className="text-sm text-gray-600 mr-2">Sort by:</label>
            <select
              id="sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="uptrend_strong">UpTrend-Strong</option>
              <option value="uptrend_weakening">UpTrend-Weakening</option>
              <option value="uptrend_near_reversal">UpTrend-NearReversal</option>
              <option value="downtrend_declining">DownTrend-Declining</option>
              <option value="downtrend_improving">DownTrend-Improving</option>
              <option value="downtrend_near_reversal">DownTrend-NearReversal</option>
              <option value="symbol_asc">Symbol (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Status Messages */}
        {(isEstimatedCount || error) && (
          <div className={`mb-6 p-4 rounded-lg ${error ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
            {isEstimatedCount && (
              <p className="text-sm mb-1">
                <span className="font-medium">Note:</span> Count is estimated for large datasets.
              </p>
            )}
            {error && (
              <p className="text-sm">
                <span className="font-medium">Error:</span> {error}
              </p>
            )}
          </div>
        )}

        {/* Top Pagination */}
        {!loading && stocks.length > 0 && (
          <Pagination
            currentPage={currentPage}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
          />
        )}

        {/* Loading State */}
        {loading ? (
          <LoadingIndicator 
            message={`Loading ${Object.keys(filters).length > 0 ? 'filtered' : 'all'} stocks...`}
            progress={loadingProgress} 
          />
        ) : stocks.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <h3 className="mt-4 text-xl font-medium text-gray-900">No stocks found</h3>
            <p className="mt-2 text-gray-500">
              Try adjusting your filters or use a different quick filter.
            </p>
            <div className="mt-6">
              <button onClick={clearFilters} className="ste-btn-primary">
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Stock Chart Grid */}
            <div className="chart-grid mb-8">
              {stocks.map((stock, index) => (
                <ChartCard 
                  key={stock.id} 
                  stock={stock} 
                  currentFilters={filters}
                  currentPage={currentPage}
                  stockIndex={(currentPage - 1) * pageSize + index}
                />
              ))}
            </div>

            {/* Bottom Pagination */}
            <Pagination
              currentPage={currentPage}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
